import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { ChevronDown, ChevronRight, GripVertical, Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import LessonItem from './LessonItem';
import type { CourseModule } from '@/services/creatorApi';

interface ModuleCardProps {
  module: CourseModule;
  moduleIndex: number;
  onUpdateModule: (module: CourseModule) => void;
  onDeleteModule: () => void;
  onAddLesson: () => void;
  onEditLesson: (lessonIndex: number) => void;
  onDeleteLesson: (lessonIndex: number) => void;
  /** Opens the inline video edit drawer for a video lesson. */
  onEditLessonVideo?: (lessonIndex: number) => void;
  onReorderLessons: (startIndex: number, endIndex: number) => void;
  dragHandleProps?: any;
}

const ModuleCard: React.FC<ModuleCardProps> = ({
  module, moduleIndex, onUpdateModule, onDeleteModule,
  onAddLesson, onEditLesson, onDeleteLesson, onEditLessonVideo, onReorderLessons,
  dragHandleProps,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(module.title);

  const saveTitle = () => {
    if (titleDraft.trim()) {
      onUpdateModule({ ...module, title: titleDraft.trim() });
    }
    setEditingTitle(false);
  };

  const handleLessonDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    onReorderLessons(result.source.index, result.destination.index);
  };

  const iconBtn: React.CSSProperties = {
    width: 28,
    height: 28,
    borderRadius: 999,
    background: 'transparent',
    border: 0,
    color: 'var(--hj-beige-ink-3)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div
      className="overflow-hidden"
      style={{
        borderRadius: 'var(--hj-r-md)',
        background: '#fff',
        border: '1px solid var(--hj-beige-line)',
      }}
    >
      <div
        className="flex items-center gap-2"
        style={{
          padding: '10px 14px',
          background: 'var(--hj-beige-hover)',
          borderBottom: expanded ? '1px solid var(--hj-beige-line)' : 'none',
        }}
      >
        <div
          {...dragHandleProps}
          className="cursor-grab active:cursor-grabbing"
          style={{ color: 'var(--hj-beige-ink-3)' }}
        >
          <GripVertical className="w-4 h-4" />
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          style={{ ...iconBtn, width: 22, height: 22 }}
          aria-label={expanded ? 'Collapse module' : 'Expand module'}
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        {editingTitle ? (
          <div className="flex items-center gap-1 flex-1">
            <Input
              value={titleDraft}
              onChange={e => setTitleDraft(e.target.value)}
              className="h-8 text-sm"
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') saveTitle();
                if (e.key === 'Escape') setEditingTitle(false);
              }}
            />
            <button type="button" onClick={saveTitle} style={iconBtn} aria-label="Save title">
              <Check className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setEditingTitle(false)}
              style={iconBtn}
              aria-label="Cancel"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <h3
              className="truncate m-0"
              style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--hj-beige-ink)' }}
            >
              {module.title}
            </h3>
            <span
              className="mono-label flex-shrink-0"
              style={{ color: 'var(--hj-beige-ink-3)' }}
            >
              {module.lessons.length} lesson{module.lessons.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        {!editingTitle && (
          <div className="flex gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                setTitleDraft(module.title);
                setEditingTitle(true);
              }}
              style={iconBtn}
              aria-label="Rename"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={onDeleteModule}
              style={{ ...iconBtn, color: 'hsl(var(--destructive))' }}
              aria-label="Delete module"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {expanded && (
        <div style={{ padding: 8 }}>
          {module.lessons.length > 0 ? (
            <DragDropContext onDragEnd={handleLessonDragEnd}>
              <Droppable droppableId={`module-${module.id}-lessons`}>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {module.lessons.map((lesson, idx) => (
                      <Draggable key={lesson.id} draggableId={lesson.id} index={idx}>
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.draggableProps}>
                            <LessonItem
                              lesson={lesson}
                              index={idx}
                              onEdit={() => onEditLesson(idx)}
                              onDelete={() => onDeleteLesson(idx)}
                              onEditVideo={onEditLessonVideo ? () => onEditLessonVideo(idx) : undefined}
                              dragHandleProps={provided.dragHandleProps}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            <p
              className="text-center m-0"
              style={{ padding: '14px 0', fontSize: 12.5, color: 'var(--hj-beige-ink-3)' }}
            >
              No lessons yet.
            </p>
          )}
          <button
            type="button"
            onClick={onAddLesson}
            className="inline-flex items-center justify-center gap-1.5 w-full"
            style={{
              marginTop: 4,
              padding: '8px 12px',
              borderRadius: 'var(--hj-r-sm)',
              background: 'transparent',
              border: '1px dashed var(--hj-beige-line)',
              color: 'var(--hj-beige-ink-3)',
              fontSize: 12.5,
              fontWeight: 500,
              fontFamily: 'inherit',
              cursor: 'pointer',
              minHeight: 36,
            }}
          >
            <Plus className="w-3.5 h-3.5" /> Add lesson
          </button>
        </div>
      )}
    </div>
  );
};

export default ModuleCard;
